import { ApiError } from '@/lib/api/errors';
import {
  isChargeExceedsBalance,
  isIdempotencyKeyRetryable,
  isIdempotencyMismatch,
} from '@/lib/api/clients/billing-v2';

/**
 * Value prefix for the "charge a new card / bank account" pseudo payment
 * method choice inside a method picker (`new:card` / `new:ach`). Shared by
 * every dialog that mounts `NewPaymentMethodCharge` (contact, invoice, job)
 * so the picker vocabulary stays identical across surfaces.
 */
const NEW_METHOD_PREFIX = 'new:';

export function newMethodValue(rail: 'card' | 'ach'): string {
  return `${NEW_METHOD_PREFIX}${rail}`;
}

export function parseNewMethodRail(value: string): 'card' | 'ach' | null {
  if (!value.startsWith(NEW_METHOD_PREFIX)) return null;
  const rail = value.slice(NEW_METHOD_PREFIX.length);
  return rail === 'card' || rail === 'ach' ? rail : null;
}

/**
 * Classifies a failed new-method charge the same way every collector dialog
 * must.
 *
 * An outcome the server never definitively answered (a lost response, a
 * timeout, a 5xx, a 429, or a 409 that is not a same-key collision) is
 * retryable with the SAME idempotency key — the charge may have gone through,
 * so the next attempt has to replay this one rather than mint a fresh charge,
 * and a one-time method must never be removed on it. A same-key mismatch is
 * equally undecided about the underlying charge it collided with, but the key
 * itself is dead either way, so the caller still mints a fresh one on the
 * next attempt (mismatch just is not itself a reason to remove the method).
 *
 * A rejection that is not an `ApiError` is not a server answer at all — it
 * says nothing about whether the charge went through — so it is ambiguous
 * too, and the key is held.
 */
export function classifyNewMethodChargeFailure(error: unknown): {
  /** Replay the SAME idempotency key on the next attempt. */
  canRetryWithSameKey: boolean;
  /** Show the ambiguous-outcome copy instead of a decline message, and never
   * remove a one-time method on this outcome. */
  isAmbiguousOutcome: boolean;
} {
  if (!(error instanceof ApiError)) {
    return { canRetryWithSameKey: true, isAmbiguousOutcome: true };
  }
  const canRetryWithSameKey = isIdempotencyKeyRetryable(error);
  const isKeyMismatch = isIdempotencyMismatch(error);
  return { canRetryWithSameKey, isAmbiguousOutcome: canRetryWithSameKey || isKeyMismatch };
}

/**
 * The 409s the operations charge paths raise BEFORE they reach the processor,
 * word for word (`gateway-payment.service.ts`). A body carries no code for
 * them — they are plain `ConflictException(message)` — so the message is the
 * only thing to match. Each means an EARLIER attempt is in flight or awaiting
 * verification, so the request that got it charged nothing:
 * - `IN_FLIGHT_REPLAY_CONFLICT_MESSAGE`: the idempotency key's payment row is
 *   still PENDING (the replay check, contact collect and invoice charge);
 * - `UNVERIFIED_CHARGE_CONFLICT_MESSAGE`: the same, parked with a lost
 *   provider answer; also the pre-submission charge guard;
 * - `IN_FLIGHT_CHARGE_CONFLICT_MESSAGE`: the pre-submission charge guard
 *   (`ChargeRefusedBeforeSubmissionException`);
 * - `CLEARING_BANK_DEBIT_CONFLICT_MESSAGE`: the same guard, when the row in
 *   flight is a bank debit that is still clearing.
 * If the server's copy drifts, nothing matches and nothing is removed.
 */
const REFUSED_BEFORE_CHARGING_MESSAGES: ReadonlySet<string> = new Set([
  'A payment is already being processed for this idempotency key',
  'An earlier charge attempt for this payment is still being verified with the payment processor. Retry after it resolves.',
  'An earlier charge attempt for this payment is still being processed. Wait for it to finish before charging again.',
  'A bank payment on this invoice is still clearing, which usually takes a few business days. It can be paid once that payment settles or is returned.',
]);

/**
 * Whether a failed charge PROVES the method it named was not charged by that
 * request: the idempotency interceptor refused it at the door
 * (`IDEMPOTENCY_KEY_MISMATCH`, only a `@RequireIdempotency` route), or the
 * charge path refused it before submission because an earlier attempt is
 * still unresolved (see `REFUSED_BEFORE_CHARGING_MESSAGES`).
 *
 * It says nothing about the EARLIER attempt, which may have charged whatever
 * it named. A caller may only act on this for a method it has never sent
 * before.
 */
export function chargeRefusedBeforeCharging(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 409) return false;
  if (isIdempotencyMismatch(error) || isChargeExceedsBalance(error)) return true;
  const message = (error.data as { message?: unknown } | null | undefined)?.message;
  return typeof message === 'string' && REFUSED_BEFORE_CHARGING_MESSAGES.has(message);
}

/**
 * Whether a 2xx charge answer is a REPLAY of an earlier attempt's charge,
 * rather than a charge of the method just sent. Both charge paths stamp the
 * charged method's provider id into `gatewayMetadata.providerPaymentMethodId`
 * before the processor call and carry it through every later write, and a key
 * that already names a committed payment returns that row untouched.
 *
 * Proof takes both halves: the stamp names a method sent EARLIER under this
 * same key, and it is not the method just sent. A stamp that merely differs
 * from the one just sent proves nothing on its own — a fresh key is never a
 * replay, and a provider reference that changed in between would read as one.
 * No stamp, or no id to compare with, proves nothing either.
 */
export function replayedEarlierMethod(
  payment: unknown,
  providerPaymentMethodId: string | null | undefined,
  sentEarlierUnderKey: ReadonlySet<string>,
): boolean {
  if (!providerPaymentMethodId) return false;
  const charged = readStampedMethod(payment);
  return (
    charged !== null && charged !== providerPaymentMethodId && sentEarlierUnderKey.has(charged)
  );
}

function readStampedMethod(payment: unknown): string | null {
  const metadata = (payment as { gatewayMetadata?: unknown } | null | undefined)?.gatewayMetadata;
  if (!metadata || typeof metadata !== 'object') return null;
  const charged = (metadata as { providerPaymentMethodId?: unknown }).providerPaymentMethodId;
  return typeof charged === 'string' && charged !== '' ? charged : null;
}

/**
 * What staff are told when the answer is a replay: the EARLIER attempt's
 * payment as it actually stands — its own amount and status — and that the
 * method just entered was not charged. `settled` is false for a status that
 * is neither a captured charge nor an accepted bank debit (a replay can
 * return a payment refunded since), so the caller can present it as news
 * rather than as a success.
 */
export function describeReplayedCharge(
  payment: unknown,
  rail: 'card' | 'ach',
  fallbackAmount: number,
  formatAmount: (amount: number) => string,
): { message: string; status: string | undefined; settled: boolean } {
  const row = (payment ?? {}) as { status?: unknown; amount?: unknown };
  const status = typeof row.status === 'string' ? row.status : undefined;
  const rowAmount =
    typeof row.amount === 'string' || typeof row.amount === 'number' ? Number(row.amount) : NaN;
  const amount = formatAmount(Number.isFinite(rowAmount) ? rowAmount : fallbackAmount);
  const notCharged = ` The ${rail === 'ach' ? 'bank account' : 'card'} you just entered was not charged.`;
  if (status === 'SUCCEEDED') {
    return {
      message: `An earlier attempt already charged ${amount}.${notCharged}`,
      status,
      settled: true,
    };
  }
  if (status === 'PENDING' && rail === 'ach') {
    return {
      message: `An earlier attempt already submitted a bank payment of ${amount} — clears in 3–5 business days.${notCharged}`,
      status,
      settled: true,
    };
  }
  const state = status ? ` (${status.toLowerCase().replace(/_/g, ' ')})` : '';
  return {
    message: `An earlier attempt already recorded a payment of ${amount}${state}.${notCharged}`,
    status,
    settled: false,
  };
}
